import React, { useState, useCallback, useEffect } from 'react';
import { fetchVehicleCatalogue, modelsOf, resolveVehicleModel } from '../services/vehicleCatalogue';
import { extractWarrantyClaim, predictClaim } from '../services/claimsApi';
import {
    createInitialFormData, emptyRow, formFromExtraction, formatClaimForApi, rowErrorKey, validateClaimForm,
} from '../components/claims/claimFormModel';
import FormCard from '../components/claims/FormCard';
import Loader from '../components/claims/ClaimLoader';
import ValidationModal from '../components/claims/ValidationModal';
import PredictionModal from '../components/claims/PredictionModal';

// Re-exported for existing importers; defined with the other prediction display rules.
export { MANUAL_REVIEW_PREFIX, needsManualReview } from '../components/claims/prediction';

const WarrentySubmitPage = () => {
    const [formData, setFormData] = useState(createInitialFormData);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('Choose file...');
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [loaderText, setLoaderText] = useState('');
    const [feedback, setFeedback] = useState({ message: '', type: '' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
    const [predictionData, setPredictionData] = useState(null);
    // Vehicle catalogue (manufacturer -> model selects). On a load failure the form falls back to a
    // clearly marked free-text model field; the backend still resolves the name exactly or reports it.
    const [catalogue, setCatalogue] = useState({ status: 'loading', data: null });
    const [manufacturerCode, setManufacturerCode] = useState('');
    const [modelNotice, setModelNotice] = useState('');

    useEffect(() => {
        let active = true;
        fetchVehicleCatalogue()
            .then((data) => active && setCatalogue({ status: 'ready', data }))
            .catch(() => active && setCatalogue({ status: 'error', data: null }));
        return () => { active = false; };
    }, []);

    const catalogueReady = catalogue.status === 'ready';

    const validateForm = useCallback(() => {
        const newErrors = validateClaimForm(formData, catalogueReady);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, catalogueReady]);

    const handleManufacturerChange = (e) => {
        setManufacturerCode(e.target.value);
        setModelNotice('');
        setFormData(prev => ({ ...prev, ModelCode: '', ModelName: '' }));
    };

    const handleModelChange = (e) => {
        const model = (catalogue.data?.models || []).find(m => m.model_code === e.target.value);
        setModelNotice('');
        setFormData(prev => ({ ...prev, ModelCode: model ? model.model_code : '', ModelName: model ? model.model_name : '' }));
        if (errors.ModelCode) setErrors(prev => ({ ...prev, ModelCode: undefined }));
    };

    // Selects the catalogue model for an extracted model name - only on an exact (resolver) match.
    const applyExtractedModel = async (extractedName) => {
        const name = (extractedName || '').trim();
        if (!catalogueReady || !name) return;
        let result = null;
        try {
            result = await resolveVehicleModel(name);
        } catch (e) {
            result = null;
        }
        if (result?.status === 'RESOLVED' && result.model) {
            setManufacturerCode(result.model.manufacturer_code);
            setFormData(prev => ({ ...prev, ModelCode: result.model.model_code, ModelName: result.model.model_name }));
            setModelNotice('');
        } else {
            setManufacturerCode('');
            setFormData(prev => ({ ...prev, ModelCode: '', ModelName: '' }));
            setModelNotice(`The document names the model "${name}", which does not match exactly one catalogue model. Please select the manufacturer and model.`);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setFileName(file.name);
        }
    };

    const handleDynamicChange = (index, event, type) => {
        const { name, value } = event.target;
        const list = [...formData[type]];
        list[index][name] = value;
        setFormData(prev => ({ ...prev, [type]: list }));

        const errorKey = rowErrorKey(type, index);
        if (errors[errorKey]) {
            setErrors(prev => ({ ...prev, [errorKey]: undefined }));
        }
    };

    const addRow = (type) => {
        setFormData(prev => ({ ...prev, [type]: [...prev[type], emptyRow(type)] }));
        if (errors[type]) {
            setErrors(prev => ({ ...prev, [type]: undefined }));
        }
    };

    const removeRow = (index, type) => {
        const list = [...formData[type]];
        list.splice(index, 1);
        setFormData(prev => ({ ...prev, [type]: list }));
    };

    const showFeedback = (message, type = 'error') => {
        setFeedback({ message, type });
        if (type === 'success') {
            setTimeout(() => setFeedback({ message: '', type: '' }), 5000);
        }
    };

    const handleReset = () => {
        setFormData(createInitialFormData());
        setSelectedFile(null);
        setFileName('Choose file...');
        setErrors({});
        setFeedback({ message: '', type: '' });
        setManufacturerCode('');
        setModelNotice('');
    };

    const handleExtract = async () => {
        if (!selectedFile) {
            showFeedback('Please select an image file to upload.');
            return;
        }
        setIsLoading(true);
        setLoaderText('Analyzing Document...');

        try {
            const response = await extractWarrantyClaim(selectedFile);
            const extractedData = response.data;
            setFormData(formFromExtraction(extractedData));
            setErrors({}); // Clear any previous errors on AI fill
            setIsValidationModalOpen(false); // Make sure the error modal doesn't linger
            await applyExtractedModel(extractedData.ModelName);
            showFeedback('AI successfully extracted data. Please review and submit.', 'success');
        } catch (error) {
            const errorMsg = error.response?.data?.detail || "Extraction failed. Please try again.";
            showFeedback(`Error: ${errorMsg}`);
        } finally {
            setIsLoading(false);
            setSelectedFile(null);
            setFileName('Choose file...');
        }
    };

    const handlePredict = async () => {
        if (!validateForm()) {
            setIsValidationModalOpen(true);
            return;
        }
        setIsLoading(true);
        setLoaderText('Predicting Status...');
        const apiData = formatClaimForApi(formData);

        try {
            const response = await predictClaim(apiData);
            setPredictionData(response.data);
            setIsModalOpen(true);
        } catch (error) {
            const errorMsg = error.response?.data?.detail || "Prediction failed. Please try again.";
            showFeedback(`Prediction Error: ${errorMsg}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) {
            setIsValidationModalOpen(true);
            return;
        }
        setIsLoading(true);
        setLoaderText('Submitting Claim...');
        setTimeout(() => {
            setIsLoading(false);
            showFeedback('Claim submitted successfully (simulated)!', 'success');
            handleReset();
        }, 1500);
    };

    const getInputClass = (fieldName) => {
        const baseClasses = "form-input transition-colors w-full";
        if (errors[fieldName]) {
            return `${baseClasses} border-red-600 focus:border-red-600 focus:ring-1 focus:ring-red-600 bg-red-50/50`;
        }
        return baseClasses;
    };

    return (
        <div className="bg-background min-h-screen font-sans">
            {isLoading && <Loader text={loaderText} />}
            <PredictionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data={predictionData} />
            <ValidationModal isOpen={isValidationModalOpen} onClose={() => setIsValidationModalOpen(false)} errors={errors} />

            <main className="container mx-auto p-4 md:p-8">
                <form onSubmit={handleSubmit} onReset={handleReset} noValidate>
                    {feedback.message && (
                        <div className={`p-4 mb-8 border ${feedback.type === 'success' ? 'bg-background border-text-main text-text-main' : 'bg-background border-text-main text-text-main'} text-sm uppercase tracking-widest font-semibold`} role="alert">
                            {feedback.message}
                        </div>
                    )}

                    <FormCard title="AI Data Extraction" icon="fa-magic">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            <div>
                                <label htmlFor="claimImageUpload" className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Upload Claim Document/Image</label>
                                <div className="mt-1 flex items-center rounded-none border-2 border-dashed border-line px-6 pt-8 pb-8 bg-background">
                                    <div className="w-full text-center">
                                        <svg className="mx-auto h-10 w-10 text-text-muted mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true"><path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        <div className="flex text-sm text-text-main justify-center uppercase tracking-widest font-semibold">
                                            <label htmlFor="claimImageUpload" className="relative cursor-pointer bg-transparent font-semibold text-text-main hover:text-text-muted focus-within:outline-none">
                                                <span className="border-b border-text-main pb-1">Upload a file</span>
                                                <input id="claimImageUpload" name="claimImageUpload" type="file" className="sr-only" onChange={handleFileChange} accept="image/*" />
                                            </label>
                                        </div>
                                        <p className="mt-4 text-xs text-text-muted tracking-wider">{fileName}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="text-center lg:text-left">
                                <button type="button" onClick={handleExtract} className="btn btn-primary w-full lg:w-auto">
                                    <i className="fas fa-sync-alt mr-3"></i>
                                    Upload & Fill Form with <span className="font-bold ml-1">AI</span>
                                </button>
                            </div>
                        </div>
                    </FormCard>

                    <FormCard title="Claim & Vehicle Information" icon="fa-car-side">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Claim Number</label><input type="text" name="ClaimNumber" value={formData.ClaimNumber} onChange={handleInputChange} className={getInputClass('ClaimNumber')} placeholder="Enter Claim Number" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Repair Order Number</label><input type="text" name="RO_number" value={formData.RO_number} onChange={handleInputChange} className={getInputClass('RO_number')} placeholder="Enter R/O Number" /></div>
                            <div>
                                <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                    VIN<span className="text-red-600 ml-1 text-sm">*</span>
                                </label>
                                <input type="text" name="VIN" value={formData.VIN} onChange={handleInputChange} className={getInputClass('VIN')} placeholder="Enter VIN" required />
                            </div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Purchasing Year</label><input type="number" name="PurchasingYear" value={formData.PurchasingYear} onChange={handleInputChange} className={getInputClass('PurchasingYear')} placeholder="e.g., 2023" /></div>
                            {catalogue.status === 'error' ? (
                                <div className="md:col-span-2">
                                    <label htmlFor="ModelName" className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                        Model Name<span className="text-red-600 ml-1 text-sm">*</span>
                                    </label>
                                    <input id="ModelName" type="text" name="ModelName" value={formData.ModelName} onChange={handleInputChange} className={getInputClass('ModelName')} placeholder="Enter the vehicle model name" required />
                                    <p role="status" className="mt-2 text-xs text-red-600 tracking-wide">
                                        The vehicle catalogue could not be loaded, so the model is entered as text. It is matched exactly against the catalogue when the status is predicted; an unmatched model is reported, not guessed.
                                    </p>
                                </div>
                            ) : (
                                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                    <div>
                                        <label htmlFor="ManufacturerCode" className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                            Manufacturer<span className="text-red-600 ml-1 text-sm">*</span>
                                        </label>
                                        <select id="ManufacturerCode" value={manufacturerCode} onChange={handleManufacturerChange} className={getInputClass('ModelCode')} disabled={!catalogueReady}>
                                            <option value="">{catalogueReady ? 'Select manufacturer' : 'Loading vehicle catalogue...'}</option>
                                            {(catalogue.data?.manufacturers || []).map(m => (
                                                <option key={m.manufacturer_code} value={m.manufacturer_code}>{m.manufacturer_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="ModelCode" className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                            Model<span className="text-red-600 ml-1 text-sm">*</span>
                                        </label>
                                        <select id="ModelCode" name="ModelCode" value={formData.ModelCode} onChange={handleModelChange} className={getInputClass('ModelCode')} disabled={!manufacturerCode}>
                                            <option value="">{manufacturerCode ? 'Select model' : 'Select a manufacturer first'}</option>
                                            {modelsOf(catalogue.data, manufacturerCode).map(m => (
                                                <option key={m.model_code} value={m.model_code}>{m.model_name} ({m.model_code})</option>
                                            ))}
                                        </select>
                                    </div>
                                    {modelNotice && (
                                        <p role="status" className="md:col-span-2 text-xs text-red-600 tracking-wide">{modelNotice}</p>
                                    )}
                                </div>
                            )}
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Mileage In</label><input type="number" name="MiledgeIn" value={formData.MiledgeIn} onChange={handleInputChange} className={getInputClass('MiledgeIn')} placeholder="e.g., 15000" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Mileage Out</label><input type="number" name="MiledgeOut" value={formData.MiledgeOut} onChange={handleInputChange} className={getInputClass('MiledgeOut')} placeholder="e.g., 15010" /></div>
                        </div>
                    </FormCard>

                    <FormCard title="Dates & Customer" icon="fa-user-clock">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">R/O Open Date</label><input type="date" name="RO_open_date" value={formData.RO_open_date} onChange={handleInputChange} className={getInputClass('RO_open_date')} /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Repair Date</label><input type="date" name="RepairDate" value={formData.RepairDate} onChange={handleInputChange} className={getInputClass('RepairDate')} /></div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                    Customer Name<span className="text-red-600 ml-1 text-sm">*</span>
                                </label>
                                <input type="text" name="CustomerName" value={formData.CustomerName} onChange={handleInputChange} className={getInputClass('CustomerName')} placeholder="Enter Customer Name" required />
                            </div>
                            <div className="md:col-span-2"><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Customer Complaint / R/O Description</label><textarea name="RO_Description" value={formData.RO_Description} onChange={handleInputChange} className={getInputClass('RO_Description')} rows="3" placeholder="Describe the customer complaint..."></textarea></div>
                        </div>
                    </FormCard>

                    <FormCard title="Technical Details" icon="fa-microchip">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
                            <div className="lg:col-span-2"><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Part Number Mainly Concerned (PNMC)</label><input type="text" name="PNMC" value={formData.PNMC} onChange={handleInputChange} className={getInputClass('PNMC')} placeholder="Enter PNMC" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">PNMC Quantity</label><input type="number" name="PNMC_Quantity" value={formData.PNMC_Quantity} onChange={handleInputChange} className={getInputClass('PNMC_Quantity')} placeholder="e.g., 1" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Warranty Type Code</label><input type="text" name="WarrantyType_Code" value={formData.WarrantyType_Code} onChange={handleInputChange} className={getInputClass('WarrantyType_Code')} placeholder="e.g., W" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Symptom Code</label><input type="text" name="SymptomCode" value={formData.SymptomCode} onChange={handleInputChange} className={getInputClass('SymptomCode')} placeholder="e.g., S01" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Damage Code</label><input type="text" name="DamageCode" value={formData.DamageCode} onChange={handleInputChange} className={getInputClass('DamageCode')} placeholder="e.g., D01" /></div>
                            <div className="md:col-span-3"><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Related Parts (comma-separated codes)</label><input type="text" name="RelatedParts" value={formData.RelatedParts} onChange={handleInputChange} className={getInputClass('RelatedParts')} placeholder="e.g., PartCodeA, PartCodeB" /></div>
                            <div className="md:col-span-3"><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Repair Location(s) (comma-separated)</label><input type="text" name="RepairLocation" value={formData.RepairLocation} onChange={handleInputChange} className={getInputClass('RepairLocation')} placeholder="e.g., Passenger side, Front bumper" /></div>
                        </div>
                    </FormCard>

                    <FormCard title="Parts Used" icon="fa-cogs"
                        headerContent={<button type="button" onClick={() => addRow('PartsUsed')} className="btn btn-secondary py-2 px-4 text-[10px]"><i className="fas fa-plus mr-2"></i> Add Part</button>}>
                        <div className="flex items-center mb-4">
                            <span className="block text-xs font-semibold text-text-muted uppercase tracking-widest">Parts Logged</span>
                            <span className="text-red-600 ml-1 text-sm">*</span>
                        </div>
                        <div className={`overflow-x-auto ${errors.PartsUsed ? 'border-2 border-red-600 p-1' : ''}`}>
                            <table className="min-w-full">
                                <thead className="bg-background border-b border-line"><tr><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Part Name</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Quantity</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Price ($)</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Action</th></tr></thead>
                                <tbody className="bg-surface divide-y divide-line">
                                    {formData.PartsUsed.map((part, index) => {
                                        const hasRowError = errors[`part_${index}`];
                                        return (
                                            <tr key={index} className="hover:bg-background transition-colors">
                                                <td className="p-2"><input type="text" name="part_name" value={part.part_name} onChange={(e) => handleDynamicChange(index, e, 'PartsUsed')} className={`form-input w-full ${hasRowError && !part.part_name ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Part Name" required /></td>
                                                <td className="p-2"><input type="number" name="part_quantity" value={part.part_quantity} onChange={(e) => handleDynamicChange(index, e, 'PartsUsed')} className={`form-input w-full ${hasRowError && (part.part_quantity === '' || part.part_quantity === undefined) ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Qty" min="0" required /></td>
                                                <td className="p-2"><input type="number" name="part_price" value={part.part_price} onChange={(e) => handleDynamicChange(index, e, 'PartsUsed')} className={`form-input w-full ${hasRowError && (part.part_price === '' || part.part_price === undefined) ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Price" step="0.01" min="0" required /></td>
                                                <td className="p-2 text-center"><button type="button" onClick={() => removeRow(index, 'PartsUsed')} className="text-text-muted hover:text-red-600 p-2 transition-colors"><i className="fas fa-trash-alt"></i></button></td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                            {formData.PartsUsed.length === 0 && (
                                <div className="p-6 text-center text-xs text-text-muted uppercase tracking-widest">
                                    No parts added yet. Click "Add Part" to begin.
                                </div>
                            )}
                        </div>
                    </FormCard>

                    <FormCard title="Labor Operations" icon="fa-hard-hat"
                        headerContent={<button type="button" onClick={() => addRow('LaborOpDetails')} className="btn btn-secondary py-2 px-4 text-[10px]"><i className="fas fa-plus mr-2"></i> Add Labor</button>}>
                        <div className="flex items-center mb-4">
                            <span className="block text-xs font-semibold text-text-muted uppercase tracking-widest">Labor Logged</span>
                            <span className="text-red-600 ml-1 text-sm">*</span>
                        </div>
                        <div className={`overflow-x-auto ${errors.LaborOpDetails ? 'border-2 border-red-600 p-1' : ''}`}>
                            <table className="min-w-full">
                                <thead className="bg-background border-b border-line"><tr><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Labor Op Code</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Labor Hours</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Technician ID</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Action</th></tr></thead>
                                <tbody className="bg-surface divide-y divide-line">
                                    {formData.LaborOpDetails.map((op, index) => {
                                        const hasRowError = errors[`labor_${index}`];
                                        return (
                                            <tr key={index} className="hover:bg-background transition-colors">
                                                <td className="p-2"><input type="text" name="LaborOpCode" value={op.LaborOpCode} onChange={(e) => handleDynamicChange(index, e, 'LaborOpDetails')} className={`form-input w-full ${hasRowError && !op.LaborOpCode ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Op Code" required /></td>
                                                <td className="p-2"><input type="number" name="LaborHours" value={op.LaborHours} onChange={(e) => handleDynamicChange(index, e, 'LaborOpDetails')} className={`form-input w-full ${hasRowError && (op.LaborHours === '' || op.LaborHours === undefined) ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Hours" step="0.1" min="0" required /></td>
                                                <td className="p-2"><input type="text" name="TechnicianID" value={op.TechnicianID} onChange={(e) => handleDynamicChange(index, e, 'LaborOpDetails')} className={`form-input w-full ${hasRowError && !op.TechnicianID ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Tech ID" required /></td>
                                                <td className="p-2 text-center"><button type="button" onClick={() => removeRow(index, 'LaborOpDetails')} className="text-text-muted hover:text-red-600 p-2 transition-colors"><i className="fas fa-trash-alt"></i></button></td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                            {formData.LaborOpDetails.length === 0 && (
                                <div className="p-6 text-center text-xs text-text-muted uppercase tracking-widest">
                                    No labor operations added yet. Click "Add Labor" to begin.
                                </div>
                            )}
                        </div>
                    </FormCard>

                    <FormCard title="Financials & Dealer Information" icon="fa-file-invoice-dollar">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Sublet Amount ($)</label><input type="number" name="SubletAmount" value={formData.SubletAmount} onChange={handleInputChange} className={getInputClass('SubletAmount')} placeholder="e.g., 50.00" step="0.01" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Sublet Code</label><input type="text" name="SubletCode" value={formData.SubletCode} onChange={handleInputChange} className={getInputClass('SubletCode')} placeholder="Enter Sublet Code" /></div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                    Estimated Total Amount ($)<span className="text-red-600 ml-1 text-sm">*</span>
                                </label>
                                <input type="number" name="EstimatedAmount" value={formData.EstimatedAmount} onChange={handleInputChange} className={getInputClass('EstimatedAmount')} placeholder="e.g., 450.50" required step="0.01" />
                            </div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Service Advisor</label><input type="text" name="ServiceAdvisor" value={formData.ServiceAdvisor} onChange={handleInputChange} className={getInputClass('ServiceAdvisor')} placeholder="Enter Service Advisor Name" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Dealer Code</label><input type="text" name="DealerCode" value={formData.DealerCode} onChange={handleInputChange} className={getInputClass('DealerCode')} placeholder="Enter Dealer Code" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Dealer Name</label><input type="text" name="DealerName" value={formData.DealerName} onChange={handleInputChange} className={getInputClass('DealerName')} placeholder="Enter Dealer Name" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Dealer Address</label><textarea name="DealerAddress" value={formData.DealerAddress} onChange={handleInputChange} className={getInputClass('DealerAddress')} rows="2" placeholder="Enter Dealer Address"></textarea></div>
                        </div>
                    </FormCard>

                    <div className="flex flex-wrap justify-end mt-12 gap-4">
                        <button type="reset" className="btn btn-secondary"><i className="fas fa-undo mr-3"></i> Reset Form</button>
                        <button type="button" onClick={handlePredict} className="btn btn-secondary"><i className="fas fa-robot mr-3"></i> Predict Status</button>
                        <button type="submit" className="btn btn-primary"><i className="fas fa-paper-plane mr-3"></i> Submit Claim</button>
                    </div>
                </form>
            </main>

            <footer className="text-center py-8 mt-12 border-t border-line">
                <p className="text-text-muted text-[10px] uppercase tracking-widest font-semibold">© {new Date().getFullYear()} YOUR COMPANY | ALL RIGHTS RESERVED</p>
            </footer>
        </div>
    );
}

export default WarrentySubmitPage;